import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  ShieldCheck, Camera, Image as ImageIcon, Lock, Globe, UsersRound,
  Heart, ThumbsUp, Laugh, Frown, Zap, Flame, Send, Trash2, MessageSquare,
  Sparkles, Palette, Pencil, X,
} from "lucide-react";
// Founder UI temporarily disabled while UI is being reworked. Components,
// hooks, and backend endpoints remain intact under
// /app/frontend/src/components/founders/ and /app/backend/routers/founders.py.
// To restore, re-add the imports + JSX at ProfileHeader.

const REACTIONS = [
  { key: "love", icon: Heart, color: "#FF1053", label: "Love" },
  { key: "like", icon: ThumbsUp, color: "#00F0FF", label: "Like" },
  { key: "haha", icon: Laugh, color: "#F59E0B", label: "Haha" },
  { key: "sad", icon: Frown, color: "#A78BFA", label: "Sad" },
  { key: "wow", icon: Zap, color: "#CCFF00", label: "Wow" },
  { key: "fire", icon: Flame, color: "#FF5900", label: "Fire" },
];

const VISIBILITIES = [
  { key: "public", icon: Globe, label: "Public" },
  { key: "friends", icon: UsersRound, label: "Friends" },
  { key: "private", icon: Lock, label: "Only me" },
];

const fileUrl = (path) => (path ? `${API}/files/${path}` : null);
const timeAgo = (iso) => {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
};

export default function Profile() {
  const { username } = useParams();
  const { user: me } = useAuth();
  const [profile, setProfile] = useState(null);
  const [posts, setPosts] = useState([]);
  const [tab, setTab] = useState("feed");
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);

  const isMe = !!(me && profile && me.username === profile.username);

  const load = async () => {
    setLoading(true);
    try {
      const p = await api.get(`/profiles/${username}`);
      setProfile(p.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load profile");
    } finally {
      setLoading(false);
    }
  };

  const loadPosts = async () => {
    if (!me) { setPosts([]); return; }
    try {
      const f = await api.get(`/posts?author=${username}&limit=30`);
      setPosts(f.data);
    } catch { /* empty */ }
  };

  useEffect(() => { load(); }, [username]);
  useEffect(() => { loadPosts(); }, [username, me?.username]);

  if (loading && !profile) {
    return <div className="min-h-screen flex items-center justify-center text-zinc-500 sc-mono">loading profile…</div>;
  }
  if (!profile) return null;

  const canView = !!me;

  return (
    <div className="min-h-screen bg-[#0A0A0C]" data-testid="profile-root">
      {/* Cover */}
      <ProfileHeader
        profile={profile}
        isMe={isMe}
        onEdit={() => setEditing(true)}
        onReload={load}
      />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 mt-6 relative z-10">
        {/* Tabs */}
        <div className="term-card p-2 mb-6 flex gap-1 flex-wrap">
          {[
            { k: "feed", label: "Feed", icon: MessageSquare },
            { k: "media", label: "Media", icon: ImageIcon },
            { k: "bling", label: "Bling", icon: Sparkles },
          ].map(({ k, label, icon: Icon }) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              data-testid={`profile-tab-${k}`}
              className={`px-4 py-2 rounded flex items-center gap-2 text-xs uppercase tracking-widest font-bold transition ${
                tab === k
                  ? "bg-[color:var(--sc-volt)] text-black"
                  : "text-zinc-400 hover:text-white hover:bg-white/5"
              }`}
            >
              <Icon className="w-3.5 h-3.5" /> {label}
            </button>
          ))}
        </div>

        {!canView && (
          <div className="term-card p-10 text-center">
            <Lock className="w-10 h-10 mx-auto mb-3 text-zinc-500" />
            <div className="sc-heading text-lg font-bold">Log in to see posts</div>
            <Link to="/login" className="sc-btn mt-4 inline-flex">Log in</Link>
          </div>
        )}

        {canView && tab === "feed" && (
          <div>
            {isMe && <PostComposer onCreated={(p) => setPosts([p, ...posts])} />}
            {posts.length === 0 ? (
              <div className="term-card p-10 text-center">
                <MessageSquare className="w-10 h-10 mx-auto mb-3 text-zinc-500" />
                <div className="sc-heading text-lg font-bold">
                  {isMe ? "You haven't posted yet." : `@${profile.username} hasn't posted yet.`}
                </div>
                <div className="text-xs text-zinc-500 mt-2">
                  {isMe ? "Drop a photo, GIF, or thought — start the feed." : "Check back soon."}
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {posts.map((p) => (
                  <PostCard
                    key={p.id}
                    post={p}
                    me={me}
                    profile={profile}
                    onDelete={() => setPosts(posts.filter((x) => x.id !== p.id))}
                    onUpdate={(next) => setPosts(posts.map((x) => (x.id === next.id ? next : x)))}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {canView && tab === "media" && <MediaGrid posts={posts} />}
        {canView && tab === "bling" && <BlingShowcase bling={profile.bling} />}
      </div>

      {editing && (
        <EditProfileModal
          profile={profile}
          onClose={() => setEditing(false)}
          onSaved={() => { setEditing(false); load(); }}
        />
      )}
    </div>
  );
}

// ============================================================
// Header + cover + avatar
// ============================================================
function ProfileHeader({ profile, isMe, onEdit }) {
  const cover = fileUrl(profile.cover_path);
  const avatar = fileUrl(profile.avatar_path);
  const bg = profile.bling?.background?.value;
  const borderColor = profile.bling?.border?.value || "rgba(255,255,255,0.15)";
  const avatarFx = profile.bling?.avatar_effect?.value;

  return (
    <div className="relative">
      <div
        className="h-52 sm:h-72 relative overflow-hidden"
        style={{ background: bg || "linear-gradient(135deg, #18181C 0%, #0A0A0C 100%)" }}
      >
        {cover && <img src={cover} alt="cover" className="w-full h-full object-cover"  loading="lazy" decoding="async" />}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#0A0A0C]" />
        <div className="crt-scanlines opacity-60" />
        {isMe && (
          <button
            onClick={onEdit}
            data-testid="profile-edit-cover-btn"
            className="absolute top-4 right-4 bg-black/60 hover:bg-black/80 border border-white/20 hover:border-[color:var(--sc-volt)] text-white text-xs font-bold px-3 py-2 rounded-md flex items-center gap-2 z-10 backdrop-blur transition"
          >
            <Camera className="w-3.5 h-3.5" /> Edit banner
          </button>
        )}
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 -mt-16 relative z-10 flex items-end gap-4 flex-wrap">
        <div
          className="w-28 h-28 sm:w-32 sm:h-32 rounded-full bg-[#18181C] flex items-center justify-center overflow-hidden relative"
          style={{
            border: `4px solid`,
            borderImage: typeof borderColor === "string" && borderColor.includes("gradient") ? `${borderColor} 1` : undefined,
            borderColor: typeof borderColor === "string" && !borderColor.includes("gradient") ? borderColor : undefined,
            boxShadow: avatarFx && !["pulse", "orbit", "shimmer_gold"].includes(avatarFx) ? avatarFx : undefined,
          }}
          data-testid="profile-avatar"
        >
          {avatar ? (
            <img src={avatar} alt="" className="w-full h-full object-cover"  loading="lazy" decoding="async" />
          ) : (
            <span className="text-4xl sc-heading font-black text-[color:var(--sc-volt)]">
              {profile.username.slice(0, 2).toUpperCase()}
            </span>
          )}
          {avatarFx === "pulse" && <div className="absolute inset-0 anim-pulse-volt rounded-full pointer-events-none" />}
          {avatarFx === "orbit" && <div className="absolute inset-[-8px] rounded-full border-2 border-dashed border-[color:var(--sc-cyan)] anim-spin-slow pointer-events-none" />}
          {avatarFx === "shimmer_gold" && <div className="absolute inset-0 anim-rainbow-glow rounded-full pointer-events-none" />}
        </div>

        <div className="flex-1 min-w-0 pb-2">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="sc-heading text-2xl sm:text-3xl font-black text-white truncate" data-testid="profile-username">
              @{profile.username}
            </h1>
            {profile.is_verified_streamer && (
              <span className="chip-neon volt" title="Verified streamer"><ShieldCheck className="w-3 h-3" /> VERIFIED</span>
            )}
            {profile.is_private && <span className="chip-neon pink" title="Private profile"><Lock className="w-3 h-3" /> PRIVATE</span>}
            {isMe && (
              <button
                onClick={onEdit}
                data-testid="profile-edit-btn"
                className="ml-auto sm:ml-2 sc-btn text-xs flex items-center gap-2 whitespace-nowrap"
              >
                <Pencil className="w-3.5 h-3.5" /> Edit profile
              </button>
            )}
          </div>
          {profile.bio && <p className="text-sm text-zinc-300 mt-2 max-w-xl leading-relaxed">{profile.bio}</p>}
          <div className="flex items-center gap-4 mt-3 sc-mono text-xs text-zinc-500">
            <span data-testid="profile-posts-count"><b className="text-white">{profile.posts_count}</b> posts</span>
            <span data-testid="profile-friends-count"><b className="text-white">{profile.friends_count}</b> friends</span>
            <span className="text-zinc-600">·</span>
            <span>{profile.user_code}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Post composer
// ============================================================
function PostComposer({ onCreated }) {
  const [body, setBody] = useState("");
  const [media, setMedia] = useState([]);
  const [visibility, setVisibility] = useState("public");
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const handleFiles = async (files) => {
    for (const f of files) {
      if (media.length >= 8) break;
      const form = new FormData();
      form.append("file", f);
      try {
        setBusy(true);
        const r = await api.post("/uploads", form, { headers: { "Content-Type": "multipart/form-data" } });
        setMedia((m) => [...m, r.data]);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Upload failed");
      } finally {
        setBusy(false);
      }
    }
  };

  const submit = async () => {
    if (!body.trim() && media.length === 0) {
      toast.error("Add text or media");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/posts", { body, media, visibility });
      setBody(""); setMedia([]); setVisibility("public");
      onCreated(r.data);
      toast.success("Posted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to post");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="term-card p-4 mb-4" data-testid="post-composer">
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="Say something to the arcade…"
        rows={3}
        maxLength={2000}
        className="sc-input resize-none"
        data-testid="post-composer-body"
      />
      {media.length > 0 && (
        <div className="mt-3 grid grid-cols-4 gap-2">
          {media.map((m, i) => (
            <div key={i} className="relative aspect-square rounded overflow-hidden bg-[#18181C]">
              {m.content_type.startsWith("video/") ? (
                <video src={fileUrl(m.path)} className="w-full h-full object-cover" />
              ) : (
                <img src={fileUrl(m.path)} alt="" className="w-full h-full object-cover"  loading="lazy" decoding="async" />
              )}
              <button
                onClick={() => setMedia(media.filter((_, x) => x !== i))}
                className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/80 flex items-center justify-center hover:bg-red-600"
                data-testid={`post-media-remove-${i}`}
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}
      <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/5 gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <input
            ref={inputRef}
            type="file"
            accept="image/*,video/*"
            multiple
            onChange={(e) => handleFiles(Array.from(e.target.files || []))}
            className="hidden"
            data-testid="post-composer-file-input"
          />
          <button
            onClick={() => inputRef.current?.click()}
            disabled={busy || media.length >= 8}
            className="sc-btn-ghost text-xs flex items-center gap-1.5"
            data-testid="post-composer-upload"
          >
            <Camera className="w-3.5 h-3.5" /> Media
          </button>
          <select
            value={visibility}
            onChange={(e) => setVisibility(e.target.value)}
            className="sc-input py-1.5 text-xs w-auto"
            data-testid="post-composer-visibility"
          >
            {VISIBILITIES.map((v) => (
              <option key={v.key} value={v.key}>{v.label}</option>
            ))}
          </select>
        </div>
        <button
          onClick={submit}
          disabled={busy || (!body.trim() && media.length === 0)}
          className="sc-btn text-xs flex items-center gap-1.5"
          data-testid="post-composer-submit"
        >
          <Send className="w-3.5 h-3.5" /> Post
        </button>
      </div>
    </div>
  );
}

// ============================================================
// Post card
// ============================================================
function PostCard({ post, me, profile, onDelete, onUpdate }) {
  const [showComments, setShowComments] = useState(false);
  const [showReactions, setShowReactions] = useState(false);
  const [busy, setBusy] = useState(false);
  const isOwner = me && (me.username === post.author_username || me.is_admin);

  const react = async (rk) => {
    if (busy) return;
    setBusy(true);
    try {
      const next = post.my_reaction === rk ? null : rk;
      const r = await api.post(`/posts/${post.id}/react`, { reaction: next });
      onUpdate(r.data);
      setShowReactions(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const doDelete = async () => {
    if (!confirm("Delete this post?")) return;
    try {
      await api.delete(`/posts/${post.id}`);
      onDelete();
      toast.success("Deleted");
    } catch (e) {
      toast.error("Failed");
    }
  };

  const VisIcon = VISIBILITIES.find((v) => v.key === post.visibility)?.icon || Globe;
  const myR = post.my_reaction ? REACTIONS.find((r) => r.key === post.my_reaction) : null;

  return (
    <div className="term-card p-4" data-testid={`post-${post.id}`}>
      <div className="flex items-center gap-3 mb-3">
        <div
          className="w-10 h-10 rounded-full bg-[#18181C] flex items-center justify-center overflow-hidden"
          style={{ border: `2px solid ${profile.bling?.border?.value || "rgba(255,255,255,0.15)"}` }}
        >
          {fileUrl(profile.avatar_path) ? (
            <img src={fileUrl(profile.avatar_path)} alt="" className="w-full h-full object-cover"  loading="lazy" decoding="async" />
          ) : (
            <span className="sc-heading text-sm font-black text-[color:var(--sc-volt)]">
              {post.author_username.slice(0, 2).toUpperCase()}
            </span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="sc-heading font-bold text-sm">@{post.author_username}</div>
          <div className="text-[10px] text-zinc-500 sc-mono flex items-center gap-1.5">
            <VisIcon className="w-3 h-3" />
            {timeAgo(post.created_at)} ago
          </div>
        </div>
        {isOwner && (
          <button
            onClick={doDelete}
            className="text-zinc-500 hover:text-red-400 transition p-1"
            data-testid={`post-delete-${post.id}`}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>

      {post.share_of && (
        <div className="mb-3 pl-3 border-l-2 border-[color:var(--sc-cyan)]/40">
          <div className="text-[10px] text-zinc-500 sc-mono mb-1">↻ shared from @{post.share_of_username}</div>
        </div>
      )}

      {post.body && <p className="text-sm text-zinc-200 whitespace-pre-wrap mb-3 leading-relaxed">{post.body}</p>}

      {post.media && post.media.length > 0 && (
        <div className={`grid gap-1.5 mb-3 ${post.media.length === 1 ? "" : post.media.length === 2 ? "grid-cols-2" : "grid-cols-3"}`}>
          {post.media.map((m, i) => (
            <div key={i} className="aspect-square rounded overflow-hidden bg-[#18181C]">
              {(m.content_type || "").startsWith("video/") ? (
                <video src={fileUrl(m.path)} controls className="w-full h-full object-cover" />
              ) : (
                <img src={fileUrl(m.path)} alt="" className="w-full h-full object-cover"  loading="lazy" decoding="async" />
              )}
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center gap-2 pt-3 border-t border-white/5 relative">
        <button
          onMouseEnter={() => setShowReactions(true)}
          onMouseLeave={() => setShowReactions(false)}
          onClick={() => react(post.my_reaction || "love")}
          className="relative flex items-center gap-1.5 text-xs sc-btn-ghost py-1.5 px-3"
          data-testid={`post-react-${post.id}`}
        >
          {myR ? (
            <>
              <myR.icon className="w-3.5 h-3.5" style={{ color: myR.color }} fill={myR.color} />
              <span style={{ color: myR.color }}>{myR.label}</span>
            </>
          ) : (
            <>
              <Heart className="w-3.5 h-3.5" /> React
            </>
          )}
          {post.total_reactions > 0 && <span className="text-zinc-500">· {post.total_reactions}</span>}

          {showReactions && (
            <div
              className="absolute bottom-full left-0 mb-2 term-card p-2 flex gap-1 z-20"
              onMouseEnter={() => setShowReactions(true)}
              onMouseLeave={() => setShowReactions(false)}
            >
              {REACTIONS.map((r) => (
                <button
                  key={r.key}
                  onClick={(e) => { e.stopPropagation(); react(r.key); }}
                  className="w-8 h-8 rounded-full flex items-center justify-center hover:scale-125 transition"
                  style={{ background: `${r.color}22` }}
                  title={r.label}
                  data-testid={`post-react-${post.id}-${r.key}`}
                >
                  <r.icon className="w-4 h-4" style={{ color: r.color }} fill={r.color} />
                </button>
              ))}
            </div>
          )}
        </button>

        <button
          onClick={() => setShowComments(!showComments)}
          className="flex items-center gap-1.5 text-xs sc-btn-ghost py-1.5 px-3"
          data-testid={`post-comment-toggle-${post.id}`}
        >
          <MessageSquare className="w-3.5 h-3.5" /> {post.comment_count > 0 ? post.comment_count : "Comment"}
        </button>
      </div>

      {showComments && <CommentsSection postId={post.id} me={me} onCountChange={(delta) => onUpdate({ ...post, comment_count: post.comment_count + delta })} />}
    </div>
  );
}

// ============================================================
// Comments
// ============================================================
function CommentsSection({ postId, me, onCountChange }) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [replyTo, setReplyTo] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let mounted = true;
    api.get(`/posts/${postId}/comments`)
      .then((r) => { if (mounted) setComments(r.data); })
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, [postId]);

  const submit = async () => {
    if (!text.trim() || busy) return;
    setBusy(true);
    try {
      const r = await api.post(`/posts/${postId}/comments`, { body: text, parent_id: replyTo });
      setComments([...comments, r.data]);
      setText(""); setReplyTo(null);
      onCountChange(1);
    } catch (e) {
      toast.error("Failed");
    } finally {
      setBusy(false);
    }
  };

  const del = async (id) => {
    if (!confirm("Delete comment?")) return;
    try {
      await api.delete(`/comments/${id}`);
      setComments(comments.filter((c) => c.id !== id));
      onCountChange(-1);
    } catch { toast.error("Failed"); }
  };

  // Group: top-level + one-level replies
  const tree = useMemo(() => {
    const roots = comments.filter((c) => !c.parent_id);
    const replies = comments.filter((c) => c.parent_id);
    return roots.map((r) => ({ ...r, replies: replies.filter((x) => x.parent_id === r.id) }));
  }, [comments]);

  return (
    <div className="mt-3 pt-3 border-t border-white/5" data-testid={`comments-${postId}`}>
      {loading ? (
        <div className="text-xs text-zinc-500 sc-mono">loading…</div>
      ) : (
        <div className="space-y-2 mb-3">
          {tree.map((c) => (
            <div key={c.id}>
              <CommentRow c={c} me={me} onDelete={() => del(c.id)} onReply={() => setReplyTo(c.id)} />
              {c.replies.length > 0 && (
                <div className="ml-8 mt-1 space-y-1">
                  {c.replies.map((rc) => (
                    <CommentRow key={rc.id} c={rc} me={me} onDelete={() => del(rc.id)} small />
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {me && (
        <div className="flex items-center gap-2">
          {replyTo && (
            <span className="chip-neon cyan cursor-pointer" onClick={() => setReplyTo(null)}>
              replying · clear ×
            </span>
          )}
          <input
            className="sc-input py-1.5 text-sm flex-1"
            placeholder="Write a comment…"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && submit()}
            maxLength={800}
            data-testid={`comment-input-${postId}`}
          />
          <button onClick={submit} disabled={busy || !text.trim()} className="sc-btn text-xs flex items-center gap-1" data-testid={`comment-submit-${postId}`}>
            <Send className="w-3 h-3" />
          </button>
        </div>
      )}
    </div>
  );
}

function CommentRow({ c, me, onDelete, onReply, small }) {
  const canDelete = me && (me.username === c.author_username || me.is_admin);
  return (
    <div className={`flex items-start gap-2 ${small ? "text-xs" : "text-sm"}`}>
      <div className={`${small ? "w-6 h-6" : "w-8 h-8"} rounded-full bg-[#18181C] flex items-center justify-center flex-shrink-0`}>
        <span className="sc-heading text-[10px] font-black text-[color:var(--sc-volt)]">
          {c.author_username.slice(0, 2).toUpperCase()}
        </span>
      </div>
      <div className="flex-1 min-w-0">
        <div className="bg-[#18181C] rounded-lg px-3 py-2 inline-block max-w-full">
          <span className="sc-heading font-bold text-xs mr-2">@{c.author_username}</span>
          <span className="text-zinc-200 break-words">{c.body}</span>
        </div>
        <div className="flex items-center gap-3 mt-1 text-[10px] sc-mono text-zinc-500">
          <span>{timeAgo(c.created_at)} ago</span>
          {onReply && <button onClick={onReply} className="hover:text-[color:var(--sc-cyan)]">Reply</button>}
          {canDelete && <button onClick={onDelete} className="hover:text-red-400">Delete</button>}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Media grid tab
// ============================================================
function MediaGrid({ posts }) {
  const items = posts.flatMap((p) => (p.media || []).map((m) => ({ ...m, post_id: p.id })));
  if (items.length === 0) {
    return (
      <div className="term-card p-10 text-center">
        <ImageIcon className="w-10 h-10 mx-auto mb-3 text-zinc-500" />
        <div className="sc-heading text-lg font-bold">No media yet.</div>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="media-grid">
      {items.map((m, i) => (
        <div key={i} className="aspect-square rounded overflow-hidden bg-[#18181C]">
          {(m.content_type || "").startsWith("video/") ? (
            <video src={fileUrl(m.path)} className="w-full h-full object-cover" muted />
          ) : (
            <img src={fileUrl(m.path)} alt="" className="w-full h-full object-cover"  loading="lazy" decoding="async" />
          )}
        </div>
      ))}
    </div>
  );
}

// ============================================================
// Bling showcase tab
// ============================================================
function BlingShowcase({ bling }) {
  const slots = [
    { key: "background", label: "Background", icon: Palette },
    { key: "border", label: "Border", icon: Sparkles },
    { key: "chat_color", label: "Chat Color", icon: MessageSquare },
    { key: "avatar_effect", label: "Avatar FX", icon: Zap },
    { key: "sticker", label: "Stickers", icon: Flame },
    { key: "emoticon", label: "Emoticons", icon: Laugh },
  ];
  const equipped = slots.filter((s) => bling?.[s.key]);
  if (equipped.length === 0) {
    return (
      <div className="term-card p-10 text-center">
        <Sparkles className="w-10 h-10 mx-auto mb-3 text-zinc-500" />
        <div className="sc-heading text-lg font-bold">No bling equipped.</div>
        <div className="text-xs text-zinc-500 mt-2">Grab items from the shop and equip them to show off.</div>
        <Link to="/shop" className="sc-btn-volt sc-btn mt-4 inline-flex">Visit Shop</Link>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="bling-showcase">
      {slots.map((s) => {
        const item = bling?.[s.key];
        return (
          <div key={s.key} className="term-card p-4">
            <div className="flex items-center gap-2 mb-3">
              <s.icon className="w-4 h-4 text-[color:var(--sc-volt)]" />
              <div className="sc-label text-[9px]">{s.label}</div>
            </div>
            {item ? (
              <BlingPreview slot={s.key} item={item} />
            ) : (
              <div className="text-xs text-zinc-500 italic">— empty slot —</div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function BlingPreview({ slot, item }) {
  if (slot === "background") {
    return (
      <div>
        <div className="h-24 rounded" style={{ background: item.value }} />
        <div className="text-xs mt-2 sc-heading font-bold">{item.name}</div>
        <div className="text-[10px] text-zinc-500 uppercase">{item.rarity}</div>
      </div>
    );
  }
  if (slot === "border") {
    return (
      <div>
        <div className="w-14 h-14 rounded-full mx-auto bg-[#18181C]" style={{ border: `3px solid`, borderImage: item.value.includes("gradient") ? `${item.value} 1` : undefined, borderColor: item.value.includes("gradient") ? undefined : item.value }} />
        <div className="text-xs mt-2 sc-heading font-bold text-center">{item.name}</div>
        <div className="text-[10px] text-zinc-500 uppercase text-center">{item.rarity}</div>
      </div>
    );
  }
  if (slot === "chat_color") {
    return (
      <div>
        <div className="text-xl sc-heading font-black" style={{ color: item.value.includes("gradient") ? "transparent" : item.value, backgroundImage: item.value.includes("gradient") ? item.value : undefined, backgroundClip: "text", WebkitBackgroundClip: "text" }}>
          @sample chat
        </div>
        <div className="text-xs mt-2 sc-heading font-bold">{item.name}</div>
        <div className="text-[10px] text-zinc-500 uppercase">{item.rarity}</div>
      </div>
    );
  }
  if (slot === "avatar_effect") {
    return (
      <div>
        <div className="w-14 h-14 rounded-full mx-auto bg-[#18181C] flex items-center justify-center" style={{ boxShadow: !["pulse", "orbit", "shimmer_gold"].includes(item.value) ? item.value : undefined }}>
          <span className="sc-heading text-xs font-black text-[color:var(--sc-volt)]">FX</span>
        </div>
        <div className="text-xs mt-2 sc-heading font-bold text-center">{item.name}</div>
        <div className="text-[10px] text-zinc-500 uppercase text-center">{item.rarity}</div>
      </div>
    );
  }
  if (slot === "sticker" || slot === "emoticon") {
    const parts = (item.value || "").split(",").map((p) => p.split("=").pop().trim()).filter(Boolean);
    return (
      <div>
        <div className="flex flex-wrap gap-1 text-xl">
          {parts.slice(0, 8).map((e, i) => <span key={i}>{e}</span>)}
        </div>
        <div className="text-xs mt-2 sc-heading font-bold">{item.name}</div>
        <div className="text-[10px] text-zinc-500 uppercase">{item.rarity}</div>
      </div>
    );
  }
  return null;
}

// ============================================================
// Edit profile modal
// ============================================================
function EditProfileModal({ profile, onClose, onSaved }) {
  const [bio, setBio] = useState(profile.bio || "");
  const [isPrivate, setIsPrivate] = useState(!!profile.is_private);
  const [avatarPath, setAvatarPath] = useState(profile.avatar_path || "");
  const [coverPath, setCoverPath] = useState(profile.cover_path || "");
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(null); // "avatar" | "cover" | null
  const avatarInp = useRef(null);
  const coverInp = useRef(null);

  const upload = async (file, setter, kind) => {
    if (!file) return;
    setUploading(kind);
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const r = await api.post(`/uploads?variant=${kind}`, form, { headers: { "Content-Type": "multipart/form-data" } });
      setter(r.data.path);
      toast.success(kind === "avatar" ? "Avatar updated" : "Cover updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      setUploading(null);
      // Reset the file input so selecting the same file again still fires onChange
      if (kind === "avatar" && avatarInp.current) avatarInp.current.value = "";
      if (kind === "cover" && coverInp.current) coverInp.current.value = "";
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.patch("/profiles/me", {
        bio, is_private: isPrivate,
        avatar_path: avatarPath || null,
        cover_path: coverPath || null,
      });
      toast.success("Profile updated");
      onSaved();
    } catch (e) {
      toast.error("Failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="term-card p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="edit-profile-modal">
        <div className="flex items-center justify-between mb-4">
          <div className="sc-heading text-xl font-black">Edit Profile</div>
          <button onClick={onClose} className="text-zinc-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        {/* Cover */}
        <div className="mb-5">
          <div className="sc-label mb-2">Cover Photo</div>
          <button
            type="button"
            onClick={() => !busy && coverInp.current?.click()}
            className="group w-full h-32 rounded relative overflow-hidden bg-[#18181C] border border-white/10 hover:border-[color:var(--sc-volt)]/60 transition block"
            disabled={busy}
            data-testid="edit-cover-thumb"
            aria-label={coverPath ? "Change cover photo" : "Upload cover photo"}
          >
            {coverPath ? (
              <img src={fileUrl(coverPath)} alt="Cover" className="w-full h-full object-cover"  loading="lazy" decoding="async" />
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center text-zinc-500">
                <Camera className="w-6 h-6 mb-1" />
                <span className="text-xs">Click to add a cover</span>
              </div>
            )}
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition flex items-center justify-center">
              <div className="opacity-0 group-hover:opacity-100 transition flex items-center gap-2 text-white text-sm font-bold">
                <Camera className="w-4 h-4" /> {coverPath ? "Change cover" : "Upload cover"}
              </div>
            </div>
            {uploading === "cover" && (
              <div className="absolute inset-0 bg-black/70 flex items-center justify-center text-xs sc-mono text-[color:var(--sc-volt)]">
                UPLOADING…
              </div>
            )}
          </button>
          <input
            ref={coverInp}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => upload(e.target.files?.[0], setCoverPath, "cover")}
            data-testid="edit-cover-input"
          />
          <div className="flex items-center gap-2 mt-2">
            <button
              type="button"
              onClick={() => coverInp.current?.click()}
              disabled={busy}
              className="sc-btn-ghost text-xs flex items-center gap-1.5"
              data-testid="edit-cover-change"
            >
              <Camera className="w-3 h-3" /> {coverPath ? "Change cover" : "Upload cover"}
            </button>
            {coverPath && (
              <button
                type="button"
                onClick={() => setCoverPath("")}
                disabled={busy}
                className="sc-btn-ghost text-xs text-[color:var(--sc-pink)] flex items-center gap-1.5"
                data-testid="edit-cover-remove"
              >
                <X className="w-3 h-3" /> Remove
              </button>
            )}
          </div>
          <p className="text-[10px] text-zinc-500 sc-mono mt-1.5">JPG / PNG / WEBP · up to 10 MB · 3:1 recommended</p>
        </div>

        {/* Avatar */}
        <div className="mb-5">
          <div className="sc-label mb-2">Avatar</div>
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={() => !busy && avatarInp.current?.click()}
              className="group relative w-20 h-20 rounded-full bg-[#18181C] overflow-hidden border-2 border-white/10 hover:border-[color:var(--sc-volt)]/60 transition flex-shrink-0"
              disabled={busy}
              data-testid="edit-avatar-thumb"
              aria-label={avatarPath ? "Change avatar" : "Upload avatar"}
            >
              {avatarPath ? (
                <img src={fileUrl(avatarPath)} alt="Avatar" className="w-full h-full object-cover"  loading="lazy" decoding="async" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-zinc-500">
                  <Camera className="w-6 h-6" />
                </div>
              )}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/60 transition flex items-center justify-center">
                <Camera className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition" />
              </div>
              {uploading === "avatar" && (
                <div className="absolute inset-0 bg-black/70 flex items-center justify-center text-[9px] sc-mono text-[color:var(--sc-volt)]">
                  UP…
                </div>
              )}
            </button>
            <div className="flex-1 min-w-0">
              <input
                ref={avatarInp}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => upload(e.target.files?.[0], setAvatarPath, "avatar")}
                data-testid="edit-avatar-input"
              />
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => avatarInp.current?.click()}
                  disabled={busy}
                  className="sc-btn-ghost text-xs flex items-center gap-1.5"
                  data-testid="edit-avatar-change"
                >
                  <Camera className="w-3 h-3" /> {avatarPath ? "Change avatar" : "Upload avatar"}
                </button>
                {avatarPath && (
                  <button
                    type="button"
                    onClick={() => setAvatarPath("")}
                    disabled={busy}
                    className="sc-btn-ghost text-xs text-[color:var(--sc-pink)] flex items-center gap-1.5"
                    data-testid="edit-avatar-remove"
                  >
                    <X className="w-3 h-3" /> Remove
                  </button>
                )}
              </div>
              <p className="text-[10px] text-zinc-500 sc-mono mt-1.5">JPG / PNG / WEBP · square works best</p>
            </div>
          </div>
        </div>

        {/* Bio */}
        <div className="mb-4">
          <div className="sc-label mb-2">Bio</div>
          <textarea value={bio} onChange={(e) => setBio(e.target.value)} rows={3} maxLength={500} className="sc-input resize-none" data-testid="edit-bio" />
        </div>

        {/* Privacy */}
        <label className="flex items-center gap-2 mb-6 cursor-pointer">
          <input type="checkbox" checked={isPrivate} onChange={(e) => setIsPrivate(e.target.checked)} data-testid="edit-private-toggle" />
          <div>
            <div className="text-sm font-bold text-white">Private profile</div>
            <div className="text-xs text-zinc-500">Only accepted friends can see your public posts.</div>
          </div>
        </label>

        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="sc-btn-ghost text-xs">Cancel</button>
          <button onClick={save} disabled={busy} className="sc-btn text-xs" data-testid="edit-profile-save">Save</button>
        </div>
      </div>
    </div>
  );
}
